import React, {useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from "react-router";
import {useTranslation} from 'react-i18next';
import {AxiosError} from 'axios';
import {Location} from 'history';
import client from 'lib/CommonApi/client';
import {callAlertWeb, callNativeWeb, footerShowEventWeb} from 'common/appInterface';
import {CardProductTabName} from "../../constants/Card";
import { useLoading } from 'components/Loading/LoadingProvider';

const BenefitsTabPage: React.FC = () => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** Params */
  const { cardProductCode = "" } = useParams();
  /** 다국어처리 */
  const { t, i18n } = useTranslation();

  const { showLoading, hideLoading } = useLoading();

  const screenId = "APPL02";
  const screenName = t("APPL02.title");

  const [productList, setProdcutList] = useState<Map<string, Product>>();

  /** 카드상품 조회 */
  const callProductList = useCallback(async () => {
    showLoading();
    await client
      .get('/api/app/product', { params: location.state.query })
      .then((response) => {
        let productMap = new Map<string, Product>();
        response.data.response
          .filter(
            (value: Product) =>
              value.useYn === 'Y' ||
              value.hasYn === 'Y' ||
              value.receiveYn === 'Y'
          )
          .forEach((value: Product) => {
            productMap.set(value.cardProductCode, value);
          });
        setProdcutList(productMap);
      })
      .catch((e: AxiosError) => {
        callAlertWeb('ERROR', '99', e.message);
      })
      .finally(() => {
        hideLoading();
      });
  }, [location, showLoading, hideLoading]);

  // 최초실행
  useEffect(() => {
    // 언어설정
    i18n.changeLanguage(location.state.query.languageCode);
    // callNative_Web 처리 (WEB > APP)
    // callNativeWeb(screenId, screenName);
    callNativeWeb(screenId, screenName, location.state.apply.linkOpenMenu === "benefitsTab" ? 'Y' : 'N');
    // 하단바 숨기기
    footerShowEventWeb("N");

    console.log("QUERY STATE");
    console.log(location.state);

    callProductList();

    // 최초한번실행 useEffect() Warning 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {productList && <BenefitsTabChild productList={productList} cardProductCode={cardProductCode} />}
    </>
  );
};

const BenefitsTabChild: React.FC<{ productList: Map<string, Product>, cardProductCode: string }> = (props) => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** Navigate */
  const navigate = useNavigate();
  /** 다국어처리 */
  const { t } = useTranslation();

  const { showLoading, hideLoading } = useLoading();

  const [cardApply, setCardApply] = useState<Partial<CardApplyStep2>>({ ...location.state.apply });
  const [cardProductCode, setCardProductCode] = useState(props.cardProductCode);

  /** backKeyEvent_App 처리 (APP > WEB) */
  const callbackBackkeyEvent = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  useEffect(() => {
    setCardApply((value) => ({ ...value, cardProductCode: cardProductCode }));
  }, [cardProductCode]);

  /** 카드신청 클릭 */
  const clickApply = useCallback(() => {
    showLoading();

    /** 고객정보 조회 */
    client
      .get('/api/app/apply/info', {
        params: { ...location.state.query, cardProductCode: cardProductCode }
      })
      .then((response) => {
        let result = response.data.response;

        if (response.data.code === '00') {
          // KYC 미인증
          if (result.kycApproved === 'N') {
            callAlertWeb('ERROR', '99', t('APPL02.kycApproved'));
          } else {
            navigate('/apply/step0', {
              state: {
                ...location.state,
                query: {
                  ...location.state.query,
                  cardProductCode: cardProductCode
                },
                apply: {
                  ...cardApply,
                  userEngName: result.userEngName,
                  phoneNo: result.phoneNo,
                  sexDivisionCode: result.sexDivisionCode,
                  birthDate: result.birthDate,
                  foreignerDivisionCode: result.foreignerDivisionCode
                }
              }
            });
          }
        } else {
          callAlertWeb('ERROR', response.data.code, response.data.message);
        }
      })
      .catch((e: AxiosError) => {
        callAlertWeb('ERROR', '99', e.message);
      })
      .finally(() => {
        hideLoading();
      });
  }, [
    location,
    navigate,
    cardApply,
    t,
    cardProductCode,
    showLoading,
    hideLoading
  ]);

  // APP이벤트 공통 수신처리 (APP > WEB)
  useEffect(() => {
    document.addEventListener("backpress", callbackBackkeyEvent);
    return () => document.removeEventListener("backpress", callbackBackkeyEvent);
  }, [callbackBackkeyEvent]);

  return (
    <div id="wrap">
      <div id="container" className="pcHidden">
        <section className="contents">
          <div className="tabWrap">
            <div className="btnTabWrap">
              {Array.from(props.productList.values()).map((value) => 
                <div key={value.cardProductCode} className={cardProductCode === value.cardProductCode ? "btnTab on" : "btnTab"} onClick={() => setCardProductCode(value.cardProductCode)}>
                  <button type="button">
                    <span>
                      {CardProductTabName[Number(value.cardProductCode)]}
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="inner">
            <div className="section serverWrap">
              <iframe className="productTabDetail" key={cardProductCode} src={`${process.env.REACT_APP_OUTER_HTML_PATH}/card/cardhtml/html/${location.state.query.languageCode}/${cardProductCode}_benefit.html`} allowFullScreen={true} title={props.productList?.get(cardProductCode)?.cardProductCodeName} />
            </div>
          </div>
        </section>
        <div className="btnArea">
          <div className="btnWrapTy01">
            <button type="button" className="btnPrimaryTy01" onClick={clickApply}
										disabled={!props.productList || props.productList?.get(cardProductCode)?.hasYn === "Y"
											|| props.productList?.get(cardProductCode)?.receiveYn === "Y"
											|| !Array.from(props.productList?.keys()).includes(cardProductCode)
										}
						>
							<span>
								{props.productList?.get(cardProductCode)?.hasYn === "Y" ? t("APPL02.exist") : (props.productList?.get(cardProductCode)?.receiveYn === "Y" ? t("APPL02.receive") : t("APPL02.apply"))}
							</span>
						</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BenefitsTabPage;